/**
 * Admin → Users (T1.1, 2026-05-28)
 *
 * Discovery-native user management surface. Closes the Discovery
 * Independence Principle gap (CLAUDE.md §14) that left standalone
 * tenants without an in-Discovery way to LIST who exists or INVITE
 * a new admin without going through Core / their IdP.
 *
 * Backend shipped earlier this session in commit c409666:
 *   GET    /v1/admin/users
 *   GET    /v1/admin/users/:id
 *   POST   /v1/admin/users          (invite - creates row status='invited')
 *   PATCH  /v1/admin/users/:id      (role / status)
 *   DELETE /v1/admin/users/:id      (soft-delete → status='suspended')
 *
 * Layout: 4 KPI tiles + filter bar + users table. Per-row actions:
 * Edit Role · Suspend / Activate · Delete. Invite-User dialog
 * collects email + full name + role.
 *
 * The 7 selectable roles match the users table CHECK constraint
 * (migration 0075). platform_* and edge_pod roles are NOT listed -
 * those are platform-internal accounts, not tenant grants.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  UserCheck,
  Users as UsersIcon,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listAdminUsers,
  inviteAdminUser,
  patchAdminUser,
  deleteAdminUser,
  type DiscoveryUser,
  type DiscoveryUserStatus,
} from '@/lib/api';

// ── Role catalog ────────────────────────────────────────────────
//
// The 6 standard tenant roles per CLAUDE.md "Tenant Roles" + custom_role.
// Mirrors users.role CHECK constraint in migration 0075. The deprecated
// alias `read_only_security_ops` is accepted by backend for back-compat
// but NOT offered here (canonical name only).

const SELECTABLE_ROLES = [
  { key: 'dpo', label: 'DPO' },
  { key: 'compliance_analyst', label: 'Compliance Analyst' },
  { key: 'legal_reviewer', label: 'Legal Reviewer' },
  { key: 'it_admin', label: 'IT Admin' },
  { key: 'security_operator', label: 'Security Operator' },
  { key: 'read_only_security_operations', label: 'Read-Only Security Operations' },
  { key: 'custom_role', label: 'Custom Role' },
] as const;

const ROLE_LABEL: Record<string, string> = Object.fromEntries(
  SELECTABLE_ROLES.map((r) => [r.key, r.label]),
);

const STATUS_VARIANT: Record<DiscoveryUserStatus, 'success' | 'warning' | 'critical'> = {
  active: 'success',
  invited: 'warning',
  suspended: 'critical',
};
const STATUS_ICON: Record<DiscoveryUserStatus, typeof CheckCircle2> = {
  active: UserCheck,
  invited: Clock,
  suspended: Ban,
};

function fmtDate(s: string | null): string {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

// ── Page ────────────────────────────────────────────────────────

export function AdminUsers(): JSX.Element {
  const [users, setUsers] = useState<DiscoveryUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<DiscoveryUserStatus | ''>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [emailSearch, setEmailSearch] = useState('');

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('compliance_analyst');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const args: { status?: DiscoveryUserStatus; role?: string; limit?: number } = {
        limit: 500,
      };
      if (statusFilter) args.status = statusFilter;
      if (roleFilter) args.role = roleFilter;
      const r = await listAdminUsers(args);
      setUsers(r.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load users');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Client-side email search (server doesn't have a full-text param yet;
  // 500-row list cap means this is cheap enough)
  const filteredUsers = useMemo(() => {
    if (!emailSearch.trim()) return users;
    const needle = emailSearch.toLowerCase();
    return users.filter(
      (u) => u.email.toLowerCase().includes(needle) || u.fullName.toLowerCase().includes(needle),
    );
  }, [users, emailSearch]);

  // KPI counts (server-side total for active filter; client-side derived from list)
  const counts = useMemo(() => {
    const c = { total: users.length, active: 0, invited: 0, suspended: 0 };
    for (const u of users) {
      if (u.status === 'active') c.active += 1;
      else if (u.status === 'invited') c.invited += 1;
      else if (u.status === 'suspended') c.suspended += 1;
    }
    return c;
  }, [users]);

  // ── Action handlers ──

  async function handleInvite(): Promise<void> {
    setInviteSubmitting(true);
    setInviteError(null);
    try {
      await inviteAdminUser({
        email: inviteEmail.trim(),
        fullName: inviteFullName.trim(),
        role: inviteRole,
      });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('compliance_analyst');
      await refresh();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'failed to invite user');
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleChangeRole(user: DiscoveryUser, nextRole: string): Promise<void> {
    setActionPending(user.id);
    try {
      await patchAdminUser(user.id, { role: nextRole });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to change role');
    } finally {
      setActionPending(null);
    }
  }

  async function handleToggleStatus(user: DiscoveryUser): Promise<void> {
    setActionPending(user.id);
    try {
      const next: DiscoveryUserStatus = user.status === 'suspended' ? 'active' : 'suspended';
      await patchAdminUser(user.id, { status: next });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to change status');
    } finally {
      setActionPending(null);
    }
  }

  async function handleDelete(user: DiscoveryUser): Promise<void> {
    // Soft-delete via DELETE endpoint (server sets status='suspended').
    // Confirmation via native confirm; production-grade swap to a modal.
    if (
      !window.confirm(
        `Soft-delete ${user.email}? Their account will be suspended (account row preserved for audit retention).`,
      )
    ) {
      return;
    }
    setActionPending(user.id);
    try {
      await deleteAdminUser(user.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to delete user');
    } finally {
      setActionPending(null);
    }
  }

  // ── Render ──

  if (loading && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Users" subtitle="Manage tenant users + roles" />
        <LoadingState caption="Loading users…" />
      </PageContainer>
    );
  }

  if (error && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Users" subtitle="Manage tenant users + roles" />
        <ErrorState
          title="Couldn't load users"
          detail={error}
          onRetry={refresh}
          severity="critical"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Users"
        subtitle="Manage tenant users + roles. Inviting a user creates their account in status=invited; they activate by setting a password via the magic-link email."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Invite user
            </Button>
          </div>
        }
      />

      {/* KPI tiles. KpiCard takes a numeric value + optional glow for
          severity tone (critical/high/medium/low). Total stays neutral
          (no glow); the lifecycle states pick a tone that matches
          their operational meaning. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total" value={counts.total} icon={UsersIcon} />
        <KpiCard label="Active" value={counts.active} icon={UserCheck} glow="low" />
        <KpiCard
          label="Invited (pending)"
          value={counts.invited}
          icon={Clock}
          glow="medium"
        />
        <KpiCard
          label="Suspended"
          value={counts.suspended}
          icon={Ban}
          glow={counts.suspended > 0 ? 'high' : undefined}
        />
      </div>

      {/* Filters */}
      <GlassCard className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Filter</CardTitle>
          <CardDescription>Narrow the list by status, role, or email/name search.</CardDescription>
        </CardHeader>
        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Status</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DiscoveryUserStatus | '')}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Role</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All</option>
              {SELECTABLE_ROLES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Search email/name</label>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="e.g. alice@example.com"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
            />
          </div>
        </div>
      </GlassCard>

      {/* Non-fatal error banner */}
      {error && users.length > 0 ? (
        <GlassCard className="mb-4 border-destructive/30">
          <div className="p-4 flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {/* Users table */}
      <GlassCard>
        <CardHeader>
          <CardTitle>
            {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
            {emailSearch || statusFilter || roleFilter ? (
              <span className="ml-2 text-xs text-muted-foreground">
                (filtered from {users.length})
              </span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <div className="px-6 pb-6 overflow-x-auto">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No users match your filters.
              {(statusFilter || roleFilter || emailSearch) && (
                <Button
                  variant="link"
                  className="ml-2"
                  onClick={() => {
                    setStatusFilter('');
                    setRoleFilter('');
                    setEmailSearch('');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="pb-3 pr-4 font-medium">User</th>
                  <th className="pb-3 pr-4 font-medium">Role</th>
                  <th className="pb-3 pr-4 font-medium">Status</th>
                  <th className="pb-3 pr-4 font-medium">Invited</th>
                  <th className="pb-3 pr-4 font-medium">Last login</th>
                  <th className="pb-3 pr-4 font-medium">MFA</th>
                  <th className="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const StatusIcon = STATUS_ICON[u.status];
                  const isBusy = actionPending === u.id;
                  return (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-b last:border-b-0 hover:bg-muted/30"
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium">{u.fullName || '-'}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {u.email}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <select
                          className="rounded-md border bg-background px-2 py-1 text-xs"
                          value={u.role}
                          disabled={isBusy}
                          onChange={(e) => handleChangeRole(u, e.target.value)}
                        >
                          {SELECTABLE_ROLES.map((r) => (
                            <option key={r.key} value={r.key}>
                              {r.label}
                            </option>
                          ))}
                          {/* If user has a role outside the selectable list (e.g. platform_*),
                              show it as a disabled option so we don't silently change it. */}
                          {!ROLE_LABEL[u.role] && (
                            <option value={u.role} disabled>
                              {u.role} (platform)
                            </option>
                          )}
                        </select>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={STATUS_VARIANT[u.status]} className="gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {u.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {fmtDate(u.invitedAt)}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {fmtDate(u.lastLoginAt)}
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {u.mfaEnrolledAt ? (
                          <Badge variant="success" className="gap-1">
                            <Shield className="h-3 w-3" />
                            On
                          </Badge>
                        ) : (
                          <Badge variant="glass" className="gap-1 text-muted-foreground">
                            <XCircle className="h-3 w-3" />
                            Off
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => handleToggleStatus(u)}
                            title={u.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                          >
                            {isBusy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : u.status === 'suspended' ? (
                              <UserCheck className="h-4 w-4" />
                            ) : (
                              <Ban className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => handleDelete(u)}
                            title="Soft-delete"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </GlassCard>

      {/* Invite dialog (lightweight inline modal - no Radix dependency
          to keep this page self-contained; matches the pattern of
          other admin pages that use native modals for simple flows). */}
      {inviteOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInviteOpen(false);
          }}
        >
          <GlassCard className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Invite user
              </CardTitle>
              <CardDescription>
                Creates the account in status=invited. The user receives a magic-link email and
                sets their password on first login.
              </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                <input
                  type="email"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="alice@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Full name</label>
                <input
                  type="text"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="Alice Sharma"
                  value={inviteFullName}
                  onChange={(e) => setInviteFullName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Role</label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {SELECTABLE_ROLES.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              {inviteError ? (
                <div className="text-sm text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {inviteError}
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviteSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInvite}
                  disabled={
                    inviteSubmitting ||
                    !inviteEmail.trim() ||
                    !inviteFullName.trim() ||
                    !inviteRole
                  }
                >
                  {inviteSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inviting…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Send invite
                    </>
                  )}
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      ) : null}
    </PageContainer>
  );
}

export default AdminUsers;
